import React from 'react';
import { cleanMetaDescription, getRobotsValue } from '../../../services/utils';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { ITourDetail, TourVisibility } from '@lialo/core/lib/models';
import { useServiceProvider } from '../../../common/Providers';
import { Language } from '@lialo/common/lib/language';
import { createProductSchema } from './productSchema';

interface Props {
  tour: ITourDetail;
}

export const TourDetailHelmet = ({ tour }: Props) => {
  const { t, i18n } = useTranslation();
  const { navigationService } = useServiceProvider();
  const isPublic = tour.visibility === TourVisibility.PUBLIC;
  const appLanguage = i18n.language as Language;

  const generateCanonicalUrl = () => {
    return navigationService.createHref(
      navigationService.generateTourDetailUrl(
        tour.shortLink,
        tour.language === Language.de ? Language.de : Language.en
      ),
      true
    );
  };

  const generateAlternateUrl = (lang: string, shortLink: string) => {
    return navigationService.createHref(
      navigationService.generateTourDetailUrl(shortLink, lang === Language.de ? Language.de : Language.en),
      true
    );
  };

  const doesAppLanguageMatchTourLanguage = () => {
    return (
      (tour.language === Language.de && appLanguage === Language.de) ||
      (tour.language !== Language.de && appLanguage !== Language.de)
    );
  };

  return (
    <Helmet script={createProductSchema(t, tour.shortLink, tour.title, tour.rating, tour.description, tour.mainImages)}>
      <title>
        {isPublic
          ? t(tour.settings?.playAtHome ? 'tour_detail.meta.play_at_home_title' : 'tour_detail.meta.title', {
              title: tour.title,
            })
          : tour.title}
      </title>
      <meta name="description" content={cleanMetaDescription(tour.description)} />
      {!isPublic && <meta name="robots" content={getRobotsValue('noindex,nofollow')} />}
      {isPublic && <link rel="canonical" href={generateCanonicalUrl()} />}
      {
        /* Only show hrefLang links, when this is the "master-page" (app lang matches tour lang)   */
        doesAppLanguageMatchTourLanguage() &&
          tour.translations.map(({ language, shortLink }) => (
            <link
              key={shortLink}
              rel="alternate"
              hrefLang={language}
              href={generateAlternateUrl(language, shortLink)}
            />
          ))
      }
    </Helmet>
  );
};
